<template>
  <div>
    <div class="nav-bar-container">
      <div class="nav-bar">
        <span class="summary-hello">{{ $t("hello") }}</span>
        <span class="summary-name">{{ renderedName }},</span>
        <span class="summary-hello">{{ $t("welcome") }}</span>
        <br>
        <label class="summary-hello emphasis">{{ $t("settings.title") }}</label>
      </div>
    </div>

    <div class="pages-settings-wrapper">
      <div
        class="pages-settings-container"
        :class="{largeForm}"
      >
        <div class="header row">
          <div
            id="pages-settings-selector"
            class="tab-container small-5 medium-5 large-3"
          >
            <HKLSelect
              :value="settingsTabValue"
              :options="settingsTabHklOptions"
              :showSearch="false"
              placeholder=""
              @change="onSettingsTabChange"
            />
          </div>
          <HKLButton
            v-if="showUnlockBtn"
            color="orange"
            action="unlockExt"
            @click="() => openUnlockModal()"
          />
        </div>

        <div class="tab-content-wrapper">
          <router-view class="tab-content" />
        </div>
      </div>
    </div>
    <EngineerUnlockModal ref="unlockModal" />
    <InviteUserModal
      ref="unlockUserModal"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { computed, getCurrentInstance } from 'vue-demi'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import { ROUTER_PATHS } from '@/constants/router'
import CmsModulesStatusMixin from '@/modules/CmsModulesStatusMixin'
import { featureSettingsMapping, defaultSettings } from '@/utils/consts'
import { aclPermsType, aclHelper } from '@/utils/aclHelper'
import HKLButton from '@/controls/Button/HKLButton.vue'
import EngineerUnlockModal from '@/modules/settings/engineers/EngineerUnlockModal.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import InviteUserModal from '@/modules/settings/engineers/InviteUserModal'

export default {
  components: {
    HKLSelect,
    HKLButton,
    EngineerUnlockModal,
    InviteUserModal,
  },
  mixins: [CmsModulesStatusMixin],
  setup () {
    const { proxy } = getCurrentInstance()

    const { modal: unlockModal, open: openUnlockModal } = useRefModal()
    const isUserPage = computed(() => {
      return proxy.component.routeName === ROUTER_PATHS.SETTING_USERS
    })
    const showUnlockBtn = computed(() => {
      return isUserPage.value && aclHelper.getSettingsUpdateAccess()
    })

    return {
      unlockModal,
      openUnlockModal,
      showUnlockBtn,
    }
  },
  data () {
    return {
      settingPermTypes: [aclPermsType.settings.read],
      settingDeptPermTypes: [
        aclPermsType.settings.read,
        aclPermsType.itqTenderDocuments.readUpdate,
        aclPermsType.thresholds.readUpdate,
        aclPermsType.departmentVendors.read,
      ],
      component: {},
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser']),
    ...mapGetters('regions', ['featureStatus']),
    renderedName () {
      return (this.currentUser || {}).name || this.$t('hklandUser')
    },
    largeForm () {
      return this.component.target === ROUTER_PATHS.USER_GUIDE_FORMS_MANAGE
    },
    options () {
      const defaultOptions = [
        {
          name: 'settings.tab.users',
          target: ROUTER_PATHS.SETTING_USERS,
          permsTypes: [
            aclPermsType.settings.read,
            aclPermsType.departmentVendors.read,
            aclPermsType.vendorECircular.manageECircularContact,
            aclPermsType.settings.readVendors,
          ],
        },
        {
          name: 'settings.tab.departments',
          target: ROUTER_PATHS.DEPARTMENT,
          permsTypes: this.settingDeptPermTypes,
        },
        {
          name: 'settings.tab.sites',
          target: ROUTER_PATHS.SITES,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.buildings',
          target: ROUTER_PATHS.BUILDING_LIST,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.security',
          target: ROUTER_PATHS.SECURITY_GROUP,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.UploadedFiles',
          target: ROUTER_PATHS.UPLOADED_FILE_SETTINGS,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.auditLog',
          target: ROUTER_PATHS.AUDIT_LOG,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.guideline',
          target: ROUTER_PATHS.GUIDELINE,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.auditReport',
          target: ROUTER_PATHS.AUDIT_REPORT,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.userGuideAndForms',
          target: ROUTER_PATHS.USER_GUIDE_FORMS_MANAGE,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.regionConfig',
          target: ROUTER_PATHS.REGION_CONFIG,
          permsTypes: [aclPermsType.settings.readRegional],
        },
        {
          name: 'settings.tab.systemMappingList',
          target: ROUTER_PATHS.SYSTEM_MAPPING_LIST,
          permsTypes: this.settingPermTypes,
        },
      ]

      return defaultOptions
    },
    filteredOptions () {
      return this.options.filter(item => {
        let isInEnabledFeature = defaultSettings.includes(item.target)
        if (!isInEnabledFeature) {
          for (let [feature, targetPath] of Object.entries(featureSettingsMapping)) {
            if (this.featureStatus[feature] && targetPath.includes(item.target)) {
              isInEnabledFeature = true
              break
            }
          }
        }
        let isEnabledAccess = item.permsTypes.some(permType => aclHelper.getAccess(permType))
        return isInEnabledFeature && isEnabledAccess
      })
    },
    // When a user accesses a detail page,
    // the dropdown-list must show a correct display name that is not existing in its options
    extendOption () {
      return [
        {
          name: 'settings.tab.buildingDetail',
          target: ROUTER_PATHS.BUILDING_PROFILE,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.siteProfile',
          target: ROUTER_PATHS.SITE_PROFILE,
          permsTypes: this.settingPermTypes,
        },
        {
          name: 'settings.tab.departmentProfile',
          target: ROUTER_PATHS.DEPARTMENT_PROFILE,
          permsTypes: this.settingPermTypes,
        },
      ]
    },
    settingsTabValue () {
      return this.component.target || undefined
    },
    settingsTabHklOptions () {
      const options = this.filteredOptions.map((item) => ({
        name: this.$t(item.name),
        value: item.target,
      }))
      const cur = this.component
      if (cur && cur.target && !options.some((o) => o.value === cur.target)) {
        options.push({
          name: this.$t(cur.name),
          value: cur.target,
          hklSelectDisplayOnly: true,
        })
      }
      return options
    },
  },
  watch: {
    $route (obj) {
      this.initDefaultComponent()
    },
  },
  mounted () {
    this.initDefaultComponent()
  },
  methods: {
    onSettingsTabChange (target) {
      const option = this.filteredOptions.find((item) => item.target === target)
      const extendOption = !option && this.extendOption.find((item) => item.target === target)
      const selectedOption = option || extendOption
      if (selectedOption) {
        this.navToComponent({
          ...selectedOption,
          params: extendOption ? this.component.params : {},
          routeName: this.$route.name,
        })
      }
    },
    getComponentFromRoute (options, extendOption) {
      const routeName = this.$route.name
      const matchedComponent = options.find(({target}) => target === routeName) ||
        extendOption.find(({target}) => target === routeName)

      const component = matchedComponent || options[0]
      component.params = this.$router.currentRoute.params || ''
      component.routeName = routeName

      return component
    },
    navToComponent (component, replaceRoute = false) {
      if (!component) return
      this.$router[replaceRoute ? 'replace' : 'push']({
        name: component.target,
        params: component.params,
      }, this.initCurrentComponent, this.initCurrentComponent)
    },
    initCurrentComponent () {
      this.component = this.getComponentFromRoute(this.options, this.extendOption)
    },
    initDefaultComponent () {
      this.initCurrentComponent()
      if (this.$route.name === 'settings') {
        this.navToComponent(this.component, true)
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "~styles/dashboard/style.scss";
@import "~styles/settings/style.scss";

.tab-container {
  padding: 15px 24px;
  .hkl-select {
    min-width: 305px;
    width: 305px;
  }
}
.tab-content {
  width: 100%;
}
.pages-settings-wrapper {
  background: white;
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-right: 25px;
  }
}
.pages-settings-container {
  max-width: 1440px;
  margin: 0 auto;
}
.largeForm {
  max-width: none;
  min-width: 1440px;
  width: 95vw;
}

</style>

<style lang="scss">
@import "~styles/vue-multiselect/multiselect-modules";
</style>
